'use client'

import { pxToRem } from '@ui/style/muiTheme'
import UIFlex from '@components/ui/UIFlex'
import {
	ArticleAreaSequenceResponseVo,
	ArticleListResponseVo,
	ArticleNewResponseVo,
	ArticleRankResponseVo,
} from '@schemas/non-auth'
import AsideArticlesWrapper from '@components/article/AsideArticlesWrapper'
import AsideArticlesItem from '@components/article/AsideArticlesItem'
import { SeeAllButton } from '@components/button/SeeAllButton'
import color from '@ui/style/color.theme'
import UILink from '@components/ui/UILink'
import PollBox, { PollBoxProps } from './PollBox'
import RelatedIssueList, { RelatedIssueListProps } from './RelatedIssueList'
import IssueTimeline, { IssueTimelineProps } from './IssueTimeline'
import { Fragment } from 'react'
import isFailedAPI from '@utils/isFailedAPI'
import ArticleAsideAd from '@components/ads/Article/Aside'
import AsideAdWrapper from '@components/ads/Article/Aside/AsideAdWrapper'
import { Taboola } from '@components/ads/Article/Taboola'
import { useIsAppControl } from '@store/isApp'
import { useMedia } from '@hooks/useMedia'

type ArticleAsideProps = RelatedIssueListProps &
	IssueTimelineProps & {
		areaSeqList: Array<ArticleAreaSequenceResponseVo>
		poll?: PollBoxProps['poll']
		newByArticle?: ArticleNewResponseVo
		important?: Array<ArticleListResponseVo>
		rankView?: ArticleRankResponseVo
		popularity?: Array<ArticleListResponseVo>
		onPollSubmit?: () => void
	}

const ArticleAside = ({
	areaSeqList,
	poll,
	relatedIssue,
	issueTimeLine,
	newByArticle,
	important,
	rankView,
	popularity,
	onPollSubmit,
}: ArticleAsideProps) => {
	const { isApp } = useIsAppControl()
	const isMediaSM = useMedia('sm')
	const switchArticleAside = (item: ArticleAreaSequenceResponseVo) => {
		const areaCode = item.articleAreaCode.code
		switch (areaCode) {
			case 'POLL':
				return (
					!isFailedAPI(poll) &&
					poll?.pollIdx && (
						<PollBox
							key={`poll-${item.areaIdx}`}
							poll={poll}
							areaDiv="ARTICLE"
							areaIdx={item.areaIdx}
							onSubmit={onPollSubmit}
						/>
					)
				)
			case 'RELATED_ISSUE':
				return (
					relatedIssue &&
					!isFailedAPI(relatedIssue) &&
					relatedIssue.length > 0 && (
						<RelatedIssueList
							key={`related-issue-${item.areaIdx}`}
							relatedIssue={relatedIssue.slice(0, 3)}
							areaDiv="ARTICLE"
							areaIdx={item.areaIdx}
						/>
					)
				)
			case 'ISSUE_TIMELINE':
				return (
					issueTimeLine &&
					!isFailedAPI(issueTimeLine) && (
						<IssueTimeline
							key={`issue-timeline-${item.areaIdx}`}
							issueTimeLine={issueTimeLine}
							areaIdx={item.areaIdx}
						/>
					)
				)
			case 'LATEST_NEWS':
				return (
					<Fragment key={`latest-news-${item.areaIdx}`}>
						<AsideAdWrapper sx={{ display: { xs: 'none', xl: 'flex' } }}>
							<ArticleAsideAd size="xl" index={0} />
						</AsideAdWrapper>
						<AsideAdWrapper sx={{ display: { xs: 'none', lg: 'flex', xl: 'none' } }}>
							<ArticleAsideAd size="lg" index={0} />
						</AsideAdWrapper>
						{newByArticle &&
							!isFailedAPI(newByArticle) &&
							(newByArticle?.newArticleList?.length ?? 0) > 0 && (
								<AsideArticlesItem
									title={`${
										newByArticle?.newArticleName
											? `${newByArticle?.newArticleName} `
											: ''
									}최신 뉴스`}
									data={newByArticle?.newArticleList?.slice(0, 5) ?? []}
									areaDiv="ARTICLE"
									areaIdx={item.areaIdx}
								/>
							)}
					</Fragment>
				)
			case 'MAIN_NEWS':
				return (
					important &&
					!isFailedAPI(important) &&
					(important?.length ?? 0) >= 3 && (
						<AsideArticlesItem
							key={`main-news-${item.areaIdx}`}
							title={item.articleAreaCode.name}
							data={important.slice(0, 4)}
							areaDiv="ARTICLE"
							areaIdx={item.areaIdx}
						/>
					)
				)
			case 'RANKING_NEWS':
				return (
					<Fragment key={`ranking-news-${item.areaIdx}`}>
						<AsideAdWrapper sx={{ display: { xs: 'none', xl: 'flex' } }}>
							<ArticleAsideAd size="xl" index={1} />
							<ArticleAsideAd size="xl" index={2} />
						</AsideAdWrapper>
						<AsideAdWrapper sx={{ display: { xs: 'none', lg: 'flex', xl: 'none' } }}>
							<ArticleAsideAd size="lg" index={1} />
							<ArticleAsideAd size="lg" index={2} />
						</AsideAdWrapper>
						{rankView &&
							!isFailedAPI(rankView) &&
							rankView?.rankArticleList &&
							rankView?.rankArticleList.length > 0 && (
								<UIFlex
									sx={{
										'&>*:not(:first-child)': {
											marginTop: pxToRem(24),
										},
									}}
								>
									<AsideArticlesItem
										title="랭킹 뉴스"
										hasRanking
										data={rankView?.rankArticleList?.slice(0, 5) ?? []}
										areaDiv="ARTICLE"
										areaIdx={item.areaIdx}
									/>

									<UIFlex alignItems="center">
										<UILink href="/rankNews">
											<SeeAllButton
												title="랭킹"
												tabIndex={-1}
												sx={{
													'&.MuiButton-root': {
														background: `${color.colWhite}`,
													},
												}}
											/>
										</UILink>
									</UIFlex>
								</UIFlex>
							)}
						<AsideAdWrapper sx={{ display: { xs: 'none', xl: 'flex' } }}>
							<ArticleAsideAd size="xl" index={3} />
						</AsideAdWrapper>
						<AsideAdWrapper sx={{ display: { xs: 'none', lg: 'flex', xl: 'none' } }}>
							<ArticleAsideAd size="lg" index={3} />
						</AsideAdWrapper>
					</Fragment>
				)
			case 'POPULAR_NEWS':
				return (
					popularity &&
					!isFailedAPI(popularity) &&
					popularity.length > 0 && (
						<AsideArticlesItem
							key={`popular-news-${item.areaIdx}`}
							title="인기 기사"
							data={popularity?.slice(0, 5) ?? []}
							areaDiv="ARTICLE"
							areaIdx={item.areaIdx}
						/>
					)
				)
			default:
				return <Fragment key={`etc-${item.areaIdx}`} />
		}
	}
	return (
		<AsideArticlesWrapper>
			{areaSeqList.map(switchArticleAside)}
			{!isApp && isMediaSM && <Taboola type="scroll" />}
		</AsideArticlesWrapper>
	)
}

export default ArticleAside
